import {
  MapBounds,
  VisualizationDefinition,
  VisualizationFilterMetadata,
  VisualizerFind,
} from './types';
import { matchesVisualizationDefinition } from './matching';

function hashString(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let value = seed || 1;

  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);

    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SyntheticVisualizerPoint extends VisualizerFind {
  synthetic: true;
  definitionId: string;
}

export function createSyntheticPoints(
  definition: VisualizationDefinition,
  finds: VisualizerFind[],
  metadata: VisualizationFilterMetadata,
  bounds: MapBounds
): SyntheticVisualizerPoint[] {
  const hasRealMatch = finds.some((find) =>
    matchesVisualizationDefinition(find, definition.filter, metadata)
  );

  if (hasRealMatch) {
    return [];
  }

  const signature = JSON.stringify({
    id: definition.id,
    filter: definition.filter,
    bounds,
  });
  const random = createRandom(hashString(signature));
  const count = 2 + Math.floor(random() * 7);
  const longitudePadding = (bounds.east - bounds.west) * 0.15;
  const latitudePadding = (bounds.north - bounds.south) * 0.15;
  const west = bounds.west + longitudePadding;
  const east = bounds.east - longitudePadding;
  const south = bounds.south + latitudePadding;
  const north = bounds.north - latitudePadding;

  return Array.from({ length: count }, (_, index) => ({
    id: `synthetic-${definition.id}-${index}`,
    synthetic: true as const,
    definitionId: definition.id,
    longitude: west + random() * (east - west),
    latitude: south + random() * (north - south),
    type: definition.filter.findTypes?.[0] || null,
    era: definition.filter.eras?.[0] || null,
    material: definition.filter.materials?.[0] || null,
    tags: definition.filter.tags || [],
  }));
}
